/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

// @mui material components
import Icon from '@mui/material/Icon';
import TextField from '@mui/material/TextField';
import DateTimeRangeContainer from 'react-advanced-datetimerange-picker';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';

function MDDateRange({ dateStart, dateStop, callBack, closeDate, dateOnly }) {
  const fmt = dateOnly ? 'DD/MM/YYYY' : 'DD/MM/YYYY HH:mm';
  const date_start = moment(new Date(dateStart));
  const date_stop = moment(new Date(dateStop));
  const { t } = useTranslation();
  const now = new Date();
  const start = moment(new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0));
  const end = dateOnly ? moment(start) : moment(start).add(1, 'days').subtract(1, 'seconds');
  const ranges = {
    'Today Only': [moment(start), moment(end)],
    'Yesterday Only': [moment(start).subtract(1, 'days'), moment(end).subtract(1, 'days')],
    '3 Days': [moment(start).subtract(3, 'days'), moment(end)],
    '5 Days': [moment(start).subtract(5, 'days'), moment(end)],
    '1 Week': [moment(start).subtract(7, 'days'), moment(end)],
    '2 Weeks': [moment(start).subtract(14, 'days'), moment(end)],
    '1 Month': [moment(start).subtract(1, 'months'), moment(end)],
    '1 Year': [moment(start).subtract(1, 'years'), moment(end)],
    '2 Years': [moment(start).subtract(2, 'years'), moment(end)],
    '3 Years': [moment(start).subtract(3, 'years'), moment(end)],
  };
  return (
    <div>
      <DateTimeRangeContainer
        ranges={ranges}
        start={date_start}
        end={date_stop}
        local={{ format: fmt, sundayFirst: false }}
        applyCallback={(start, stop) => {
          callBack(start, stop);
        }}
      >
        <TextField
          fullWidth
          value={`${date_start.format(fmt)} 
          ${t(`element.to`)} 
          ${date_stop.format(fmt)}`}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Icon>calendar_month</Icon>&nbsp; {t('element.date')} :
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton onClick={closeDate}>
                  <Icon>highlight_off</Icon>
                </IconButton>
              </InputAdornment>
            ),
            autoComplete: 'off',
            readOnly: true,
          }}
        />
      </DateTimeRangeContainer>
    </div>
  );
}

// Typechecking props for the MDDatePicker
MDDateRange.defaultProps = {
  dateOnly: false,
};

MDDateRange.propTypes = {
  dateStart: PropTypes.string.isRequired,
  dateStop: PropTypes.string.isRequired,
  callBack: PropTypes.func.isRequired,
  closeDate: PropTypes.func.isRequired,
  dateOnly: PropTypes.bool,
};

export default MDDateRange;
